import React from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { Button, Card, DatePicker, Input, Layout, PageHeader, Select, Spin, Switch, Table, Tag, Tooltip, message } from 'antd'

import services from '@services/surveys'
import * as styles from './style.scss'

const NO_REFERENT = '__none__'
const PER_PAGE = 20

// column widths in percent, change here (should add up to 100%)
const COL_WIDTH = {
    request: '6%',
    client: '8%',
    referent: '8%',
    question: '12.5%', // each question column (4 questions = 50%)
    score: '8%',
    status: '8%',
    answered: '8%',
    expand: '4%'
}

const STATUS = {
    answered: { text: 'נענה', color: 'green' },
    sms_not_sent: { text: 'SMS לא נשלח', color: 'red' },
    expired: { text: 'פג תוקף', color: 'orange' },
    waiting: { text: 'ממתין', color: 'blue' }
}

const emptyFilters = {
    user_service_id: '',
    user_id: '',
    referent: undefined,
    include_unanswered: false,
    from: null,
    to: null
}

// 0-2 red, 3 amber, 4-5 green. Answer 0 is a real score, so check null only
const chipClass = answer => {
    if (answer === null || answer === undefined) return styles.empty
    if (answer <= 2) return styles.red
    if (answer === 3) return styles.amber
    return styles.green
}
// label is the shown text when it differs from the number used for the color (final score)
const Chip = ({ answer, label }: { answer: any; label?: any }) => (
    <span className={`${styles.chip} ${chipClass(answer)}`}>
        {answer === null || answer === undefined ? '-' : label !== undefined ? label : answer}
    </span>
)

// slots qN are matched by question_id, so reordered questions do not mix answers
const slotsOf = row =>
    [1, 2, 3, 4]
        .filter(n => row[`q${n}_question_id`] !== null && row[`q${n}_question_id`] !== undefined)
        .map(n => ({
            question_id: row[`q${n}_question_id`],
            answer: row[`q${n}_answer`],
            comment: row[`q${n}_comment`]
        }))
const answerFor = (row, questionId) => {
    const slot = slotsOf(row).find(s => s.question_id === questionId)
    return slot ? slot.answer : null
}

// final score = average of the answered questions. 0 is a real answer, so check null only
const finalScoreOf = row => {
    const answers = slotsOf(row)
        .map(s => s.answer)
        .filter(a => a !== null && a !== undefined)
    return answers.length ? answers.reduce((sum, a) => sum + a, 0) / answers.length : null
}
// same chip for a score: color from the rounded number, text with 1 decimal
const ScoreChip = ({ score }) =>
    score === null ? <Chip answer={null} /> : <Chip answer={Math.round(score)} label={score.toFixed(1)} />

// color by answer rate: green high, orange medium, red low
const rateColor = (p: number) => (p >= 70 ? '#16a34a' : p >= 40 ? '#e77910' : '#dc2626')

// styles for the stat cards and the active-filter chips
const STATS_CSS = `
.d5-tags-row{display:flex;align-items:center;flex-wrap:wrap;gap:6px;margin-top:12px;padding-top:12px;border-top:1px dashed #e8e8e8}
.d5-tags-label{font-size:12px;color:#888;margin-left:4px}
.d5-tag{margin:0 !important;background:#fdf3e6 !important;border-color:#f0c88f !important;color:#8a520a !important}
.d5-clear-all{font-size:12px;color:#d07f13;cursor:pointer;margin-right:4px}
.d5-card-title{display:flex;align-items:center;gap:8px;font-size:15px}
.d5-card-count{font-size:12px;color:#888;font-weight:400}
.d5-stats-card{position:relative;display:flex;align-items:center;background:#fff;border-radius:8px;padding:14px 8px;margin-bottom:16px}
.d5-rate-card,.d5-score-card{flex:1;display:flex;align-items:center;justify-content:center;gap:16px}
.d5-rate-head,.d5-score-head{display:flex}
.d5-rate-label,.d5-score-label{font-size:14px;color:#595959;font-weight:600}
.d5-rate-value,.d5-score-value{font-size:48px;font-weight:700;line-height:1.1;font-variant-numeric:tabular-nums}
.d5-score-max{color:#bfbfbf;font-weight:600}
.d5-rate-dots,.d5-score-dots{display:flex;flex-direction:column;gap:2px;font-size:13px;color:#595959;font-variant-numeric:tabular-nums}
.d5-rate-dots-answered,.d5-rate-dots-missing,.d5-score-dots-scored{display:inline-flex;align-items:center}
.d5-rate-dots-dot-answered,.d5-rate-dots-dot-missing,.d5-score-dots-dot-scored{display:inline-block;width:8px;height:8px;border-radius:50%;margin-left:6px}
.d5-rate-dots-dot-answered{background:#16a34a}
.d5-rate-dots-dot-missing{background:#dc2626}
.d5-stats-divider{width:1px;align-self:stretch;background:#f0f0f0}
.d5-stats-scope{position:absolute;top:6px;left:12px;font-size:12px;color:#888}
`

export default class SurveyResults extends React.Component<any, any> {
    state: any = {
        filters: { ...emptyFilters },
        view: 'results', // 'results' | 'ranking'
        ranking: [],
        rows: [],
        total: 0,
        stats: { sent: 0, answered: 0, percent: 0, avg_score: null, scored: 0 },
        page: 1,
        questions: [],
        referents: [],
        loading: false
    }

    componentDidMount() {
        this.load(1, this.state.filters)
    }

    // -1 on unmount, so a late response never calls setState
    componentWillUnmount() {
        this.reqId = -1
    }

    // id of the newest request; older responses that come back later are ignored
    reqId = 0
    load = async (page, filters) => {
        const params: any = { page, per_page: PER_PAGE }
        if (filters.user_service_id) params.user_service_id = filters.user_service_id
        if (filters.user_id) params.user_id = filters.user_id
        if (filters.referent) params.referent = filters.referent
        if (filters.from) params.from = filters.from.format('YYYY-MM-DD')
        if (filters.to) params.to = filters.to.format('YYYY-MM-DD')
        if (filters.include_unanswered) params.include_unanswered = true
        this.appliedFilters = filters
        const id = ++this.reqId
        this.setState({ loading: true })
        try {
            const { data } = await services.getResults(params)
            if (id !== this.reqId) return
            this.setState({
                rows: data.data,
                total: data.total,
                stats: data.stats,
                page: data.page,
                questions: data.questions,
                referents: data.referents,
                loading: false
            })
        } catch (e) {
            if (id !== this.reqId) return
            message.error('שגיאה בטעינת תוצאות הסקר')
            this.setState({ loading: false })
        }
    }

    // the referent and "show unanswered" filters are not sent: ranking is always answered surveys of all referents
    loadRanking = async filters => {
        const params: any = {}
        if (filters.user_service_id) params.user_service_id = filters.user_service_id
        if (filters.user_id) params.user_id = filters.user_id
        if (filters.from) params.from = filters.from.format('YYYY-MM-DD')
        if (filters.to) params.to = filters.to.format('YYYY-MM-DD')
        this.appliedFilters = filters
        const id = ++this.reqId
        this.setState({ loading: true })
        try {
            const { data } = await services.getReferentScores(params)
            if (id !== this.reqId) return
            this.setState({ ranking: data.data, loading: false })
        } catch (e) {
            if (id !== this.reqId) return
            message.error('שגיאה בטעינת דירוג הרפרנטים')
            this.setState({ loading: false })
        }
    }

    setFilter = (name, value) => this.setState({ filters: { ...this.state.filters, [name]: value } })
    // filters of the last search, so paging ignores boxes that were edited but not searched
    appliedFilters: any = { ...emptyFilters }
    search = () => {
        const { filters, view } = this.state
        // a picked referent in the ranking view opens the results table for him
        if (view === 'ranking' && !filters.referent) return this.loadRanking(filters)
        this.setState({ view: 'results' })
        this.load(1, filters)
    }
    reset = () => {
        this.setState({ filters: { ...emptyFilters } })
        if (this.state.view === 'ranking') this.loadRanking(emptyFilters)
        else this.load(1, emptyFilters)
    }
    toggleView = () => {
        const view = this.state.view === 'results' ? 'ranking' : 'results'
        this.setState({ view })
        if (view === 'ranking') this.loadRanking(this.appliedFilters)
        else this.load(1, this.appliedFilters)
    }
    // click on a referent name: show only his user services
    showReferent = name => {
        const filters = { ...this.appliedFilters, referent: name }
        // clear old rows so only the loading spinner shows until the filter finishes
        this.setState({ view: 'results', filters, rows: [], loading: true })
        this.load(1, filters)
    }

    questionText = id => {
        const q = this.state.questions.find(item => item.id === id)
        return q ? q.text : 'שאלה שנמחקה'
    }

    // chips for the filters that are really applied (last search), not what is typed in the boxes
    activeFilters = () => {
        const f = this.appliedFilters
        const ranking = this.state.view === 'ranking'
        const list: any[] = []
        if (f.user_service_id) list.push({ key: 'user_service_id', label: `בקשה: ${f.user_service_id}` })
        if (f.user_id) list.push({ key: 'user_id', label: `לקוח: ${f.user_id}` })
        if (!ranking && f.referent)
            list.push({ key: 'referent', label: `רפרנט: ${f.referent === NO_REFERENT ? 'ללא רפרנט' : f.referent}` })
        if (f.from || f.to) list.push({ key: 'dates', label: `תאריכים: ${this.scopeText()}` })
        if (!ranking && f.include_unanswered) list.push({ key: 'include_unanswered', label: 'כולל ללא מענה' })
        return list
    }
    // stats change only by dates, so say which dates they cover
    scopeText = () => {
        const { from, to } = this.appliedFilters
        if (from && to) return `${from.format('DD/MM/YY')} – ${to.format('DD/MM/YY')}`
        if (from) return `מ-${from.format('DD/MM/YY')}`
        if (to) return `עד ${to.format('DD/MM/YY')}`
        return 'כל הזמנים'
    }
    // remove one chip: clear it in the boxes and in the applied filters, then reload
    removeFilter = key => {
        const clear = key === 'dates' ? { from: null, to: null } : { [key]: emptyFilters[key] }
        const next = { ...this.appliedFilters, ...clear }
        this.setState({ filters: { ...this.state.filters, ...clear } })
        if (this.state.view === 'ranking' && !next.referent) this.loadRanking(next)
        else {
            this.setState({ view: 'results' })
            this.load(1, next)
        }
    }
    renderTags = (tagClass, clearClass) => {
        const list = this.activeFilters()
        return [
            ...list.map(t => (
                <Tag
                    key={t.key}
                    className={tagClass}
                    closable
                    onClose={e => {
                        // the list re-renders without this chip, so antd does not need to hide it
                        e.preventDefault()
                        this.removeFilter(t.key)
                    }}
                >
                    {t.label}
                </Tag>
            )),
            list.length > 1 && (
                <a key="clear" className={clearClass} onClick={this.reset}>
                    נקה הכל
                </a>
            )
        ]
    }
    statNums = () => {
        const { stats } = this.state
        const p = Number(stats.percent) || 0
        const score = stats.avg_score === null || stats.avg_score === undefined ? null : Number(stats.avg_score)
        return {
            p,
            pColor: rateColor(p),
            answered: Number(stats.answered) || 0,
            sent: Number(stats.sent) || 0,
            score,
            scoreText: score === null ? '-' : score.toFixed(1),
            scoreColor: score === null ? '#888' : rateColor(score * 20),
            scored: Number(stats.scored) || 0
        }
    }
    // big stat cards above the filters; they change only by dates, so the scope text says which dates
    renderStatCards = () => {
        const n = this.statNums()
        return (
            <div className="d5-stats-card">
                <div className="d5-rate-card">
                    <div className="d5-rate-head">
                        <span className="d5-rate-label">אחוז מענה</span>
                    </div>
                    <div className="d5-rate-value" style={{ color: n.pColor }}>
                        {n.p}%
                    </div>
                    <div className="d5-rate-dots">
                        <span className="d5-rate-dots-answered">
                            <i className="d5-rate-dots-dot-answered" />
                            {n.answered} נענו
                        </span>
                        <span className="d5-rate-dots-missing">
                            <i className="d5-rate-dots-dot-missing" />
                            {n.sent - n.answered} לא נענו
                        </span>
                    </div>
                </div>
                <div className="d5-stats-divider" />
                <div className="d5-score-card">
                    <div className="d5-score-head">
                        <span className="d5-score-label">ציון סופי ממוצע</span>
                    </div>
                    <div className="d5-score-value" style={{ color: n.scoreColor }}>
                        {n.scoreText}
                        <span className="d5-score-max">/5</span>
                    </div>
                    <div className="d5-score-dots">
                        <span className="d5-score-dots-scored">
                            <i className="d5-score-dots-dot-scored" style={{ background: n.scoreColor }} />
                            {n.scored} סקרים
                        </span>
                    </div>
                </div>
                <span className="d5-stats-scope">{this.scopeText()}</span>
            </div>
        )
    }

    renderFilters() {
        const { filters, referents, view } = this.state
        const count = this.activeFilters().length
        return (
            <div className={styles.filters}>
                <div className={styles.filtersTitle}>סינון:</div>
                <div className={styles.filtersRow}>
                    {/* first child = far right, the page is rtl */}
                    <Input
                        style={{ width: 140, marginLeft: 8 }}
                        placeholder="מזהה בקשה"
                        value={filters.user_service_id}
                        onChange={e => this.setFilter('user_service_id', e.target.value)}
                    />
                    <Input
                        style={{ width: 140, marginLeft: 8 }}
                        placeholder="מזהה לקוח"
                        value={filters.user_id}
                        onChange={e => this.setFilter('user_id', e.target.value)}
                    />
                    <Select
                        showSearch
                        allowClear
                        style={{ width: 200, marginLeft: 8 }}
                        placeholder="רפרנט"
                        value={filters.referent}
                        onChange={v => this.setFilter('referent', v)}
                    >
                        <Select.Option value={NO_REFERENT}>ללא רפרנט</Select.Option>
                        {referents.map(name => (
                            <Select.Option key={name} value={name}>
                                {name}
                            </Select.Option>
                        ))}
                    </Select>
                    <DatePicker
                        style={{ width: 140, marginLeft: 8 }}
                        placeholder="מתאריך"
                        value={filters.from}
                        onChange={v => this.setFilter('from', v)}
                    />
                    <DatePicker
                        style={{ width: 140, marginLeft: 8 }}
                        placeholder="עד תאריך"
                        value={filters.to}
                        onChange={v => this.setFilter('to', v)}
                    />
                    {view === 'results' && (
                        <span style={{ marginLeft: 8 }}>
                            הצג ללא מענה{' '}
                            <Switch
                                checked={filters.include_unanswered}
                                // the api filters unanswered rows now, so reload right away like before
                                onChange={v => {
                                    this.setFilter('include_unanswered', v)
                                    this.load(1, { ...this.appliedFilters, include_unanswered: v })
                                }}
                            />
                        </span>
                    )}
                    <Button type="primary" onClick={this.search} style={{ marginLeft: 8 }}>
                        חיפוש
                    </Button>
                    <Button onClick={this.reset}>איפוס</Button>
                    <Button
                        onClick={this.toggleView}
                        style={{ marginRight: 'auto', background: '#1890ff', borderColor: '#1890ff', color: '#fff' }}
                    >
                        {view === 'results' ? 'דירוג רפרנטים' : 'תוצאות סקר'}
                    </Button>
                </div>
                {count > 0 && (
                    <div className="d5-tags-row">
                        <span className="d5-tags-label">מסננים פעילים:</span>
                        {this.renderTags('d5-tag', 'd5-clear-all')}
                    </div>
                )}
            </div>
        )
    }

    // expanded details: question text, score, comment and final comment
    renderDetails = row => (
        <div className={styles.details}>
            {slotsOf(row).map(s => (
                <div key={s.question_id} className={styles.detailsRow}>
                    <strong>{this.questionText(s.question_id)}</strong> <Chip answer={s.answer} />
                    {s.comment && <div className={styles.comment}>הערה: {s.comment}</div>}
                </div>
            ))}
            {row.final_comment && (
                <div className={styles.detailsRow}>
                    <strong>הערה סופית:</strong> {row.final_comment}
                </div>
            )}
            {row.status !== 'answered' && this.statusTag(row)}
        </div>
    )

    statusTag = row => <Tag color={STATUS[row.status].color}>{STATUS[row.status].text}</Tag>
    clientCell = row => (
        <span>
            {row.first_name} {row.last_name}
            <br />
            <Link to={`/clients/${row.user_id}`}>{row.user_id}</Link>
        </span>
    )
    // date on the first line, time on a new line. utc() keeps the saved Israel time as is
    dateCell = value => {
        if (!value) return '-'
        const m = moment.utc(value)
        return (
            <span>
                {m.format('DD/MM/YYYY')}
                <br />
                {m.format('HH:mm')}
            </span>
        )
    }
    answeredDate = row => (row.status === 'answered' ? this.dateCell(row.updated_at) : '-')

    columns(): any[] {
        const cols: any[] = [
            { title: 'בקשה', width: COL_WIDTH.request, dataIndex: 'user_service_id' },
            { title: 'לקוח', width: COL_WIDTH.client, render: (_, row) => this.clientCell(row) },
            {
                title: 'רפרנט',
                width: COL_WIDTH.referent,
                render: (_, row) =>
                    row.referent_full_name ? (
                        <Tooltip title="לחץ לסינון לפי רפרנט" mouseEnterDelay={0}>
                            <a
                                style={{ color: '#e77910', cursor: 'pointer' }}
                                onClick={e => {
                                    // do not expand the row
                                    e.stopPropagation()
                                    this.showReferent(row.referent_full_name)
                                }}
                            >
                                {row.referent_full_name}
                            </a>
                        </Tooltip>
                    ) : (
                        '-'
                    )
            }
        ]
        this.state.questions.forEach(q =>
            cols.push({
                title: q.text,
                width: COL_WIDTH.question,
                render: (_, row) => <Chip answer={answerFor(row, q.id)} />
            })
        )
        cols.push({
            title: 'ציון סופי',
            width: COL_WIDTH.score,
            render: (_, row) => <ScoreChip score={finalScoreOf(row)} />
        })
        cols.push({ title: 'סטטוס', width: COL_WIDTH.status, render: (_, row) => this.statusTag(row) })
        cols.push({ title: 'נענה', width: COL_WIDTH.answered, render: (_, row) => this.answeredDate(row) })
        // empty last column: the expand "+" is drawn inside it
        cols.push({ title: '', width: COL_WIDTH.expand, render: () => null })
        return cols
    }

    rankingColumns = (): any[] => [
        { title: '#', width: '8%', render: (_, __, index) => index + 1 },
        {
            title: 'רפרנט',
            render: (_, row) => (
                <Tooltip title="לחץ לסינון לפי רפרנט" mouseEnterDelay={0}>
                    <a style={{ color: '#e77910', cursor: 'pointer' }} onClick={() => this.showReferent(row.referent)}>
                        {row.referent}
                    </a>
                </Tooltip>
            )
        },
        { title: 'ציון ממוצע', render: (_, row) => <ScoreChip score={row.score} /> },
        { title: 'מספר סקרים', dataIndex: 'count' }
    ]

    render() {
        const { rows, total, page, loading, view, ranking } = this.state
        const columns = this.columns()
        return (
            <Layout>
                <style>{STATS_CSS}</style>
                <PageHeader onBack={() => window.history.back()} title="תוצאות סקר" />
                <div className={styles.page}>
                    {this.renderStatCards()}
                    {this.renderFilters()}
                    <Card
                        bordered={false}
                        title={
                            <span className="d5-card-title">
                                {view === 'ranking' ? 'דירוג רפרנטים' : 'תוצאות'}
                                <span className="d5-card-count">{view === 'ranking' ? ranking.length : total} שורות</span>
                            </span>
                        }
                    >
                        {view === 'ranking' ? (
                            <Table
                                rowKey="referent"
                                loading={loading && ranking.length > 0}
                                locale={loading ? { emptyText: <Spin /> } : undefined}
                                dataSource={ranking}
                                columns={this.rankingColumns()}
                                pagination={false}
                            />
                        ) : (
                            <Table
                                rowKey="id"
                                // no rows yet: show the spinner in the first row, no faded overlay
                                loading={loading && rows.length > 0}
                                locale={loading ? { emptyText: <Spin /> } : undefined}
                                dataSource={rows}
                                columns={columns}
                                expandRowByClick
                                expandIconAsCell={false}
                                expandIconColumnIndex={columns.length - 1}
                                expandedRowRender={this.renderDetails}
                                pagination={{
                                    current: page,
                                    total,
                                    pageSize: PER_PAGE,
                                    onChange: p => this.load(p, this.appliedFilters)
                                }}
                            />
                        )}
                    </Card>
                </div>
            </Layout>
        )
    }
}
